<script setup lang="ts">
import { cn } from '@/utils';
import {
	PropertyTypeEnum,
	propertyTypes,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import type { RegionData } from '~~/core/entities/Region';
import type { LinkClickedPayload, RegionClickedPayload } from '.';
import TextLink from '../ui/text-link/TextLink.vue';
interface Props extends RegionData {
	class?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
	(e: 'region-clicked', payload: RegionClickedPayload): void;
	(e: 'link-clicked', payload: LinkClickedPayload): void;
}>();

function getPropertyTypeLink(propertyType: string) {
	const propertyTypeObject = propertyTypes.find(
		type => type.identifier === propertyType
	);

	const link = PropertyFiltersPresenter.getPathFromFilters({
		states: [props.state],
		cities: [props.city],
		regions: [props.region],
		propertyTypes: [propertyTypeObject?.identifier!]
	});

	return link;
}

function formatPropertyTypes(types: PropertyTypeEnum[]): {
	type: string;
	link: string;
}[] {
	return types.map(type => ({
		type: propertyTypesTranslations[
			type as keyof typeof propertyTypesTranslations
		],
		link: getPropertyTypeLink(type)
	}));
}

const formattedPropertyTypes = computed(() =>
	formatPropertyTypes(props.topPropertyTypes)
);

const link = computed(() =>
	PropertyFiltersPresenter.getPathFromFilters({
		states: [props.state],
		cities: [props.city],
		regions: [props.region]
	})
);

function handleRegionClicked() {
	emit('region-clicked', {
		neighborhood_name: props.region,
		destination_url: link.value
	});
}

const { emit: emitLinkClicked } =
	useEventBus<LinkClickedPayload>('link-clicked');

function handleLinkClicked(propertyType: string) {
	emitLinkClicked({
		destination_url: getPropertyTypeLink(propertyType),
		neighborhood_name: props.region,
		link_text: propertyType
	});
}
</script>

<template>
	<NuxtLink
		:to="link"
		class="mb-4 flex size-full items-center"
		@click="handleRegionClicked"
	>
		<div
			:class="
				cn(
					'flex size-full min-h-[280px] cursor-pointer flex-col justify-between rounded-lg bg-secondary p-6 transition hover:border-primary hover:bg-secondary-dark-1',
					props.class
				)
			"
		>
			<div class="flex w-full items-center justify-start gap-2">
				<h3 class="text-xl font-medium text-primary">
					{{ props.region }}
				</h3>
				<IconMaterialSymbolsNorthEast class="w-5 text-primary" />
			</div>
			<div class="flex flex-col gap-4">
				<p class="mb-2 text-base font-medium text-primary">
					{{ props.propertyCount }} imóveis
				</p>
				<div class="flex flex-wrap gap-4">
					<NuxtLink
						v-for="type in formattedPropertyTypes"
						:key="type.type"
						v-slot="{ href }"
						custom
						:to="type.link"
						@click.stop="handleLinkClicked(type.type)"
					>
						<TextLink
							as="a"
							:href="href"
							variant="default"
							class="text-sm text-primary"
						>
							{{ type.type }}
						</TextLink>
					</NuxtLink>
				</div>
			</div>
		</div>
	</NuxtLink>
</template>
